import { apiRequest } from "./request";
import { AppDispatch } from "@/store/store";
import { setUserCredits } from "@/store/user/slice";
import { IStructuredJobData } from "@/types";
import { AiFeedbackRequest, AIModel } from "@/types/ai";
import { IAtsScanResult } from "@/types/atsScan";
import { CreditActionCode } from "@/types/creditAction";
import { ICredit, User } from "@/types/user";

export interface AiResponse {
    output: string;
    userUpdates?: Partial<User>;
    modelUsed?: number;
}

export interface AiResumeSummaryRequest {
    content?: string;
    industries?: string[];
    jobRoles?: string[];
    experienceLevel?: string;
    overridePrompt?: string;
    temperature?: number;
    mockResponse?: boolean;
    adminId?: string;
    sectionType?: string;
    model?: AIModel;
}

const mockDelay = () => new Promise(resolve => setTimeout(resolve, 1000));

const postProcessAiResponse = (
    userCredits: ICredit | undefined,
    dispatch: AppDispatch,
) => {
    if (userCredits) {
        dispatch(setUserCredits(userCredits));
    }
};

const generateParagraphPro = async (
    params: AiResumeSummaryRequest,
    signal?: AbortSignal,
    dispatch?: AppDispatch,
) => {
    if (params.mockResponse) {
        await mockDelay();
        return Promise.resolve({
            output: "Results-driven software engineer with 5+ years of experience in full-stack development. Specialized in building scalable web applications using React, Node.js, and cloud technologies. Proven track record of delivering high-quality solutions and mentoring junior developers. Strong focus on clean code practices and performance optimization.",
        } as AiResponse);
    }
    const { userUpdates, ...rest } = await apiRequest<AiResponse>(
        `/ai/paragraph-pro`,
        "POST",
        params,
        undefined,
        undefined,
        signal,
    );
    if (dispatch && userUpdates?.credits) {
        postProcessAiResponse(userUpdates.credits, dispatch);
    }
    return rest;
};

export interface AiBulletPointsRequest {
    content?: string;
    sectionName?: string;
    overridePrompt?: string;
    temperature?: number;
    mockResponse?: boolean;
    sectionType?: string;
    adminId?: string;
    industries?: string[];
    jobRoles?: string[];
    experienceLevel?: string;
    model?: AIModel;
}

const generateBulletBuilder = async (
    params: AiBulletPointsRequest,
    signal?: AbortSignal,
    dispatch?: AppDispatch,
) => {
    if (params.mockResponse) {
        await mockDelay();
        return Promise.resolve({
            output: "• Developed and maintained multiple high-traffic web applications using React and TypeScript\n• Implemented CI/CD pipelines reducing deployment time by 40%\n• Led a team of 5 developers in delivering a major platform upgrade\n• Optimized database queries resulting in 30% improved performance",
        } as AiResponse);
    }
    const { userUpdates, ...rest } = await apiRequest<AiResponse>(
        `/ai/generate-bullet-points`,
        "POST",
        params,
        undefined,
        undefined,
        signal,
    );
    if (dispatch && userUpdates?.credits) {
        postProcessAiResponse(userUpdates.credits, dispatch);
    }
    return rest;
};

export interface AiQuickPromptRequest {
    content?: string;
    temperature?: number;
    mockResponse?: boolean;
    creditActionCode?: CreditActionCode;
    adminId?: string;
    sectionType?: string;
    model?: AIModel;
}

const generateQuickPrompt = async (
    params: AiQuickPromptRequest,
    signal?: AbortSignal,
    dispatch?: AppDispatch,
) => {
    if (params.mockResponse) {
        await mockDelay();
        return Promise.resolve({
            output: `Mock response for ${params.creditActionCode}`,
        } as AiResponse);
    }
    const { userUpdates, ...rest } = await apiRequest<AiResponse>(
        `/ai/write-prompt`,
        "POST",
        params,
        undefined,
        undefined,
        signal,
    );
    if (dispatch && userUpdates?.credits) {
        postProcessAiResponse(userUpdates.credits, dispatch);
    }
    return rest;
};

const createAiFeedback = async (params: AiFeedbackRequest) => {
    await apiRequest<AiResponse>(`/ai/feedback`, "POST", params);
};

const getJobInsights = async (params: { jobDescription: string }) => {
    const { jobInsights, isJobDescription } = await apiRequest<{
        jobInsights: IStructuredJobData;
        isJobDescription: boolean;
    }>(`/ai/job`, "POST", params);
    return { jobInsights, isJobDescription };
};

const scanResume = async (
    params: {
        resume: string;
        scanModes?: string[];
        model?: AIModel;
        temperature?: number;
    },
    dispatch: AppDispatch,
) => {
    const { scanFeedback, score, userUpdates } = await apiRequest<{
        scanFeedback: Record<string, unknown>;
        score: number;
        userUpdates: Partial<User>;
    }>(`/ai/scan`, "POST", params);
    if (userUpdates) {
        postProcessAiResponse(userUpdates.credits, dispatch);
    }
    return { scanFeedback, score };
};

const getResumesAtsScanResults = async (resumeId: string) => {
    const { scanResults } = await apiRequest<{
        scanResults: IAtsScanResult[];
    }>(`/ai/scan/${resumeId}`, "GET");
    return scanResults;
};

export default {
    generateParagraphPro,
    generateBulletBuilder,
    generateQuickPrompt,
    createAiFeedback,
    getJobInsights,
    scanResume,
    getResumesAtsScanResults,
};
